import React, {useEffect, useState} from 'react';
import {Card} from "../../components/Card";
import UboldSpinner from "../../ubold-components/UboldSpinner";
import {SelectOption, useSelectFilterManager, useTextFilterManager} from "../../platform/search/PlatformSearch";
import {AdditionalSelectFilter, AdditionalTextFilter} from "../../components/search/MetronicSearchComponents";
import useImpellerAssemblySearch, {AssemblySearchRequest, BLADE_MANUFACTURING_TYPES, BladeManufacturingTypeCode, MechanicalSelectionChoiceJson, MechanicalSelectionH3RangeEstimationJson} from "./ImpellerAssemblyService";
import {useFetchAllObjs} from "platform/hooks/PlatformTypeScriptHooks";
import {FormattedMechanicalSelectionCalculationLog} from "./FormattedMechanicalSelectionCalculationLog";
import {ImpellerAssemblyResultsForForm} from "./ImpellerAssemblyResultsForForm";
import {ErrorTreePresenter} from "./ErrorTreePresenter";
import {WentechMeasuringStandards} from "../../business/measurement/WentechMeasurementStandards";
import {LabeledEditor, useOptionalMeasurementEditor} from "../selection/useFanCriteria";
import {createInternationalizationPrefixer} from "../../platform/i18n/i18n";
import {ImpellerAssemblyH3EstimationResultCard} from "./ImpellerAssemblyH3EstimationResultCard";
import {useShaftDimensionsEditors} from "../../mechanicalconfig/useShaftDimensionsEditors";
import {useSearchParams} from "react-router-dom";
import {AssemblyPrecisePredefinedCriteria, decodeAssemblyCriteriaFromBase64} from "./DatasheetToAssemblyPredefinedCriteria";
import {SimulationRequestJson} from "../../business/ApiJsonTypes";
import {AssemblyCriteriaDownloader} from "./criteriaexport/AssemblyCriteriaDownloader";
import {AssemblyCriteriaUploader} from "./criteriaexport/AssemblyCriteriaUploader";
import {useHasCurrentUserPrivilege} from "../../layout/Protected";


const t = createInternationalizationPrefixer("FanSelectionCriteriaControls");

function useQueryParamPredefinedAssemblyCriteria(): AssemblyPrecisePredefinedCriteria | null {
    const [searchParams] = useSearchParams();
    const predefinedParam = searchParams.get("predefined");
    
    if (predefinedParam) {
        return decodeAssemblyCriteriaFromBase64(predefinedParam);
    }
    
    return null;
}

export function ImpellerAssemblyPage() {

    const availableShaftDiameters: number[] | null = useFetchAllObjs<number[]>("/api/mechanical/available-shaft-diameters/all");
    if (availableShaftDiameters == null) {
        return <div className={"text-center mt-5"}><UboldSpinner/></div>;
    }
    return <ImpellerAssemblyPageLoaded availableShaftDiameters={availableShaftDiameters}/>;
}

export function ImpellerAssemblyPageLoaded(props: {
    availableShaftDiameters: number[]
}) {

    const predefinedCriteria = useQueryParamPredefinedAssemblyCriteria();
    const [uploadedCriteria, setUploadedCriteria] = useState<AssemblySearchRequest | null>(null);

    // Demo mechanical data only participates in assembly with PERM_DEVELOPMENT_TOOLS (backend enforces this too).
    const hasDevTools = useHasCurrentUserPrivilege("PERM_DEVELOPMENT_TOOLS");

    if ( predefinedCriteria !== null ) {
        console.log("Predefined criteria", predefinedCriteria);
    }

    if ( uploadedCriteria !== null ) {
        console.log("Uploaded criteria", uploadedCriteria);
    }
    /// NEW UI - gradualy migrating to reuse shared criteria components



    // Convert predefined criteria to format expected by useShaftDimensionsEditors
    const simulationRequestForShaftEditors = predefinedCriteria ? {
        mechanicalPreferences: {
            shaftDiameter: predefinedCriteria.shaftDiameter,
            shaftLength: predefinedCriteria.shaftLength,
            customShaftThreadPreference: predefinedCriteria.customShaftThreadPreference,
            expectedImpellerHeight: predefinedCriteria.expectedImpellerHeight,
            fanMountingOrientationCode: null,
            airflowRelativeClutchMountingSideCode: null,
            preferredSupportAndClutchDiscSideCode: null
        }
    } as SimulationRequestJson : null;

    const {shaftDiameterEditor, shaftLengthEditor, screwMEditor, threadLengthEditor} = useShaftDimensionsEditors(props.availableShaftDiameters, simulationRequestForShaftEditors,
        ()=> {})

    const impellerHeightEditor = useOptionalMeasurementEditor(WentechMeasuringStandards.IMPELLER_HEIGHT, false);

    // Populate impeller height from predefined criteria
    useEffect(() => {
        if (predefinedCriteria?.expectedImpellerHeight != null) {
            impellerHeightEditor.populate(predefinedCriteria.expectedImpellerHeight);
            impellerHeightEditor.enable();
        }
    }, [predefinedCriteria?.expectedImpellerHeight]);

    /// OLD UI


    const availableBladeProfiles = useFetchAllObjs<string[]>("/api/mechanical-blade-profiles");

    const bladeCountFilterManager = useTextFilterManager("bladeCount", "Ilość łopat", true,  "", true);
    const bladeManufacturingFilterManager = useSelectFilterManager("bladeManufacturing", "Wykonanie łopat", BLADE_MANUFACTURING_TYPES.map<SelectOption>(bmt => ({
        label: bmt.name,
        value: bmt.typeCode
    })), true, predefinedCriteria?.bladeManufacturing);
    const impellerDiameterFilterManager = useTextFilterManager("impellerDiameter", "Średnica wirnika [mm]", true, predefinedCriteria?.impellerDiameter?.toString() || "", true);
    //const shaftDiameterFilterManager = useTextFilterManager("shaftDiameter", "Średnica wału [mm]", true, "", true);
    //const shaftLengthFilterManager = useTextFilterManager("shaftLength", "Długość wału [mm]", true, "", true);
    //const expectedImpellerHeightFilterManager = useTextFilterManager("expectedImpellerHeight", "Oczekiwana wysokość wirnika [mm]", true, "", true);
    //const bladeProfileDisplayNameFilterManager = useTextFilterManager("bladeProfileDisplayName", "Profil łopaty np. (14)");


    const fetchedProfilesAsOptions: {
        label: string,
        value: string
    }[] = availableBladeProfiles !== null ? (availableBladeProfiles.map(p => ({label: p, value: p}))) : [];
    const bladeProfileDisplayNameFilterManager = useSelectFilterManager("bladeProfileDisplayName", "Profil łopaty", [
        {label: "Dowolny", value: ""},
        ...fetchedProfilesAsOptions
    ], true , "");

    const bladeAngleFilterManager = useTextFilterManager("bladeAngle", "Kąt łopat [°]", true, predefinedCriteria?.bladeAngle?.toString() || "", true);

    // temporarily not displayed
    const betaIncrementationFilterManager = useTextFilterManager("betaIncrementation", "Powiększenie kąta na znaczniku [°]", true, "", true);

    const rpmFilterManager = useTextFilterManager("rpm", "RPM [min-1]", true, predefinedCriteria?.rpm?.toString() || "", true);
    const bladeEndSpeedFilterManager = useTextFilterManager("bladeEndSpeed", "Prędkość końca łopaty [m/s]", true, "", true);
    const userEnforcedDiscThicknessFilterManager = useTextFilterManager("userEnforcedDiscThickness", "Wymuszona grubość tarczy [mm]", true, "", true);
    const powerAtWorkingPointFilterManager = useTextFilterManager("powerAtWorkingPoint", "Moc w punkcie pracy [kW]", true, predefinedCriteria?.powerAtWorkingPoint?.toString() || "", true);
    const totalPressureAtWorkingPointFilterManager = useTextFilterManager("totalPressureAtWorkingPoint", "Ciśnienie całkowite w punkcie pracy [Pa]", true, predefinedCriteria?.totalPressureAtWorkingPoint?.toString() || "", true);
    const adjustableImpellerIdFilterManager = useTextFilterManager("adjustableImpellerId", "ID Wirnika", true, predefinedCriteria?.adjustableImpellerId || "", false);
    const softStartPresentFilterManager = useSelectFilterManager("softStartPresent", "Obecny softstart/falownik", [
        {value: "false", label: "Nie"},
        {value: "true", label: "Tak"}
    ], true, predefinedCriteria?.softStartPresent !== undefined ? predefinedCriteria!.softStartPresent.toString() : "false");
    const realizeWithClutchFilterManager = useSelectFilterManager("realizeWithClutch", "Wykonanie ze sprzęgłem", [
        {value: "true", label: "Ze sprzęgłem"},
        {value: "false", label: "Bez sprzęgła"}
    ], true, predefinedCriteria?.realizeWithClutch !== undefined ? predefinedCriteria!.realizeWithClutch.toString() : "true");

    const ignoreMaximumTestedClutchManufacturingsFilterManager = useSelectFilterManager("softStartPresent", "Ignoruj 'Maks. ilość testowanych sprzęgieł", [
        {value: "false", label: "Nie"},
        {value: "true", label: "Ignoruj"}
    ]);
    const preferredConstructionMaterialCodeFilterManager = useSelectFilterManager("preferredConstructionMaterialCode", "Materiał cz. konstrukcyjncych", [
        {label: "HDG (Wykonanie standardowe)", value: "HDG"},
        {label: "A2", value: "A2"},
        {label: "A4", value: "A4"},
        {label: "AISI 316", value: "AISI 316"},
        {label: "AISI 316L", value: "AISI 316L"},
    ], true, predefinedCriteria?.preferredConstructionMaterialCode || "HDG");
    const preferredFastenersMaterialCodeFilterManager = useSelectFilterManager("preferredFastenersMaterialCode", "Materiał cz. złącznych", [
        {label: "HDG (Wykonanie standardowe)", value: "HDG"},
        {label: "A2", value: "A2"},
        {label: "A4", value: "A4"},
        {label: "AISI 316", value: "AISI 316"},
        {label: "AISI 316L", value: "AISI 316L"},
    ], true, predefinedCriteria?.preferredFastenersMaterialCode || "HDG");
    // TODO Fetch from backend, now there is duplication
    const fanMountingPositionNameFilterManager = useSelectFilterManager("fanMountingPositionName", "Pozycja wirnika", [
        {value: "HSI1", label: "HSI1 - Poziomo, sprzęgło na wlocie, podpory strona przeciwna do sprzęgła"},
        {value: "HSI2", label: "HSI2 - Poziomo, sprzęgło na wlocie, podpory strona sprzęgła"},
        {value: "HSO1", label: "HSO1 - Poziomo, sprzęgło na wylocie, podpory strona przeciwna do sprzęgła"},
        {value: "HSO2", label: "HSO2 - Poziomo, sprzęgło na wylocie, podpory strona sprzęgła"},
        {value: "VSI1", label: "VSI1 - Pionowo, sprzęgło na wlocie, podpory strona przeciwna do sprzęgła"},
        {value: "VSI2", label: "VSI2 - Pionowo, sprzęgło na wlocie, podpory strona sprzęgła"}
    ], true, predefinedCriteria?.fanMountingPositionName || "HSI1");
    // TODO Fetch from backend, now there is duplication
    const auditSeverityFilterManager = useSelectFilterManager("auditSeverityName", "Rygor audytu mechaniki", [
        {value: "NO_OVERLOAD_ALLOWED", label: "Nie pozwalaj na przeciążenia (domyślne)"},
        {value: "OVERLOADED_CLUTCH_ALLOWED", label: "Pozwól tylko na przeciążone sprzęgło"},
        {value: "OVERLOADED_CLUTCH_AND_VON_MISES_ALLOWED", label: "Pozwól przeciążyć sprzęgło i czop"},
        {value: "ALL_OVERLOADS_ALLOWED", label: "Dopuść wszystkie przeciążenia (czop, sprzęgło, czop-płat)"},
        {value: "ALL_OVERLOADS_AND_MISSING_PIN_BLADESHEET_ALLOWED", label: "Dopuść wszystkie, a nawet brak danych czop-płat (z W40)"}
    ]);
    const allowDemoDataFilterManager = useSelectFilterManager("allowDemoData", "Użycie przykładowych danych demo", [
        {value: "false", label: "Nie"},
        {value: "true", label: "Tak"}
    ]);
    const allowTooLongClutch = useSelectFilterManager("allowTooLongClutch", "Pozwól przekroczyć maks. dł. sprzęgła", [
        {value: "false", label: "Nie"},
        {value: "true", label: "Tak"}
    ]);

    const [criteria, setCriteria] = useState<undefined | AssemblySearchRequest>();

    useEffect(() => {
        if (uploadedCriteria === null) {
            return;
        }

        console.log("Populating filter managers with uploaded criteria", uploadedCriteria);

        if (uploadedCriteria.bladeCount !== null) {
            bladeCountFilterManager.prepopulate(uploadedCriteria.bladeCount.toString());
        }
        if (uploadedCriteria.adjustableImpellerId !== null) {
            adjustableImpellerIdFilterManager.prepopulate(uploadedCriteria.adjustableImpellerId);
        }
        bladeManufacturingFilterManager.prepopulate(uploadedCriteria.bladeManufacturing);
        impellerDiameterFilterManager.prepopulate(uploadedCriteria.impellerDiameter.toString());
        fanMountingPositionNameFilterManager.prepopulate(uploadedCriteria.fanMountingPositionName);
        auditSeverityFilterManager.prepopulate(uploadedCriteria.auditSeverityName);
        preferredConstructionMaterialCodeFilterManager.prepopulate(uploadedCriteria.preferredConstructionMaterialCode);
        preferredFastenersMaterialCodeFilterManager.prepopulate(uploadedCriteria.preferredFastenersMaterialCode);

        if (uploadedCriteria.bladeProfileDisplayName !== null) {
            bladeProfileDisplayNameFilterManager.prepopulate(uploadedCriteria.bladeProfileDisplayName);
        }
        if (uploadedCriteria.bladeAngle !== null) {
            bladeAngleFilterManager.prepopulate(uploadedCriteria.bladeAngle.toString());
        }
        if (uploadedCriteria.betaIncrementation !== null) {
            betaIncrementationFilterManager.prepopulate(uploadedCriteria.betaIncrementation.toString());
        }
        if (uploadedCriteria.rpm !== null) {
            rpmFilterManager.prepopulate(uploadedCriteria.rpm.toString());
        }
        if (uploadedCriteria.bladeEndSpeed !== null) {
            bladeEndSpeedFilterManager.prepopulate(uploadedCriteria.bladeEndSpeed.toString());
        }
        if (uploadedCriteria.userEnforcedDiscThickness !== null) {
            userEnforcedDiscThicknessFilterManager.prepopulate(uploadedCriteria.userEnforcedDiscThickness.toString());
        }
        if (uploadedCriteria.powerAtWorkingPoint !== null) {
            powerAtWorkingPointFilterManager.prepopulate(uploadedCriteria.powerAtWorkingPoint.toString());
        }
        if (uploadedCriteria.totalPressureAtWorkingPoint !== null) {
            totalPressureAtWorkingPointFilterManager.prepopulate(uploadedCriteria.totalPressureAtWorkingPoint.toString());
        }
        if (uploadedCriteria.softStartPresent !== null) {
            softStartPresentFilterManager.prepopulate(uploadedCriteria.softStartPresent.toString());
        }
        realizeWithClutchFilterManager.prepopulate(uploadedCriteria.realizeWithClutch.toString());
        if (uploadedCriteria.ignoreMaximumTestedClutchManufacturings !== null) {
            ignoreMaximumTestedClutchManufacturingsFilterManager.prepopulate(uploadedCriteria.ignoreMaximumTestedClutchManufacturings.toString());
        }
        if (uploadedCriteria.allowDemoData !== null) {
            allowDemoDataFilterManager.prepopulate(uploadedCriteria.allowDemoData.toString());
        }
        if (uploadedCriteria.allowTooLongClutch !== null) {
            allowTooLongClutch.prepopulate(uploadedCriteria.allowTooLongClutch.toString());
        }

        // Populate shaft dimension editors
        if (uploadedCriteria.shaftDiameter !== null) {
            shaftDiameterEditor.populate(uploadedCriteria.shaftDiameter.toString());
            shaftDiameterEditor.enable();
        }
        if (uploadedCriteria.shaftLength !== null) {
            shaftLengthEditor.populate(uploadedCriteria.shaftLength);
            shaftLengthEditor.enable();
        }
        if (uploadedCriteria.customShaftThreadPreference !== null) {
            screwMEditor.populate(uploadedCriteria.customShaftThreadPreference.customScrewM);
            screwMEditor.enable();
            threadLengthEditor.populate(uploadedCriteria.customShaftThreadPreference.customDlugoscGwintuWCzopieWalu);
            threadLengthEditor.enable();
        }
        if (uploadedCriteria.expectedImpellerHeight !== null) {
            impellerHeightEditor.populate(uploadedCriteria.expectedImpellerHeight);
            impellerHeightEditor.enable();
        }

        // Clear the uploaded criteria state after populating
        setUploadedCriteria(null);
    }, [uploadedCriteria]);

    const {
        result: {
            choices,
            calculations: mainCalculations,
            h3RangeEstimation,
            errorTree
        },
        loading
    } = useImpellerAssemblySearch(criteria);


    useEffect(() => {

        const bladeCountOrImpellerIdGiven = (bladeCountFilterManager.currentFilterOrNull?.value !== undefined && parseInt(bladeCountFilterManager.currentFilterOrNull.value as string) > 0)
        ||(adjustableImpellerIdFilterManager.currentFilterOrNull?.value !== undefined && adjustableImpellerIdFilterManager.currentFilterOrNull.value)

        if ( bladeCountOrImpellerIdGiven &&
            impellerDiameterFilterManager.currentFilterOrNull?.value !== undefined && parseInt(impellerDiameterFilterManager.currentFilterOrNull.value as string) > 0 &&
            (bladeManufacturingFilterManager.currentFilterOrNull?.value !== undefined && bladeManufacturingFilterManager.currentFilterOrNull.value) &&
            (preferredConstructionMaterialCodeFilterManager.currentFilterOrNull?.value !== undefined && preferredConstructionMaterialCodeFilterManager.currentFilterOrNull.value) &&
            (preferredFastenersMaterialCodeFilterManager.currentFilterOrNull?.value !== undefined && preferredFastenersMaterialCodeFilterManager.currentFilterOrNull.value) &&
            (fanMountingPositionNameFilterManager.currentFilterOrNull?.value !== undefined && fanMountingPositionNameFilterManager.currentFilterOrNull.value) &&
            (auditSeverityFilterManager.currentFilterOrNull?.value !== undefined && auditSeverityFilterManager.currentFilterOrNull.value)
        ) {

            setCriteria({
                bladeCount: bladeCountFilterManager.currentFilterOrNull === null ? null : bladeCountFilterManager.currentFilterOrNull.value as number,
                adjustableImpellerId: adjustableImpellerIdFilterManager.currentFilterOrNull === null ? null : adjustableImpellerIdFilterManager.currentFilterOrNull.value as string,
                impellerDiameter: parseInt(impellerDiameterFilterManager.currentFilterOrNull.value as string),
                bladeManufacturing: bladeManufacturingFilterManager.currentFilterOrNull.value as BladeManufacturingTypeCode,
                shaftDiameter: shaftDiameterEditor.value === null ? null : parseInt(shaftDiameterEditor.value as string),
                shaftLength: shaftLengthEditor.value === null ? null : shaftLengthEditor.value,
                expectedImpellerHeight: impellerHeightEditor.value === null ? null : impellerHeightEditor.value,
                customShaftThreadPreference: (screwMEditor.value === null && threadLengthEditor.value === null) ? null : {customDlugoscGwintuWCzopieWalu: threadLengthEditor.value!, customScrewM: screwMEditor.value!},
                bladeProfileDisplayName: bladeProfileDisplayNameFilterManager.currentFilterOrNull === null ? null : bladeProfileDisplayNameFilterManager.currentFilterOrNull.value as string,
                bladeAngle: bladeAngleFilterManager.currentFilterOrNull === null ? null : parseFloat(bladeAngleFilterManager.currentFilterOrNull.value as string),
                betaIncrementation: betaIncrementationFilterManager.currentFilterOrNull === null ? null : parseFloat(betaIncrementationFilterManager.currentFilterOrNull.value as string),
                preferredConstructionMaterialCode: preferredConstructionMaterialCodeFilterManager.currentFilterOrNull.value as string,
                preferredFastenersMaterialCode: preferredFastenersMaterialCodeFilterManager.currentFilterOrNull.value as string,
                rpm: rpmFilterManager.currentFilterOrNull === null ? null : parseInt(rpmFilterManager.currentFilterOrNull.value as string),
                bladeEndSpeed: bladeEndSpeedFilterManager.currentFilterOrNull === null ? null : parseFloat(bladeEndSpeedFilterManager.currentFilterOrNull.value as string),
                powerAtWorkingPoint: powerAtWorkingPointFilterManager.currentFilterOrNull === null ? null : parseFloat(powerAtWorkingPointFilterManager.currentFilterOrNull.value as string),
                totalPressureAtWorkingPoint: totalPressureAtWorkingPointFilterManager.currentFilterOrNull === null ? null : parseFloat(totalPressureAtWorkingPointFilterManager.currentFilterOrNull.value as string),
                softStartPresent: softStartPresentFilterManager.currentFilterOrNull === null ? null : softStartPresentFilterManager.currentFilterOrNull.value as string === "true",
                realizeWithClutch: realizeWithClutchFilterManager.currentFilterOrNull === null ? false : realizeWithClutchFilterManager.currentFilterOrNull.value as string === "true",
                ignoreMaximumTestedClutchManufacturings: ignoreMaximumTestedClutchManufacturingsFilterManager.currentFilterOrNull === null ? null : ignoreMaximumTestedClutchManufacturingsFilterManager.currentFilterOrNull.value as string === "true",
                fanMountingPositionName: fanMountingPositionNameFilterManager.currentFilterOrNull.value as string,
                auditSeverityName: auditSeverityFilterManager.currentFilterOrNull.value as string,
                allowDemoData: allowDemoDataFilterManager.currentFilterOrNull === null ? null : allowDemoDataFilterManager.currentFilterOrNull.value as string === "true",
                allowTooLongClutch: allowTooLongClutch.currentFilterOrNull === null ? null : allowTooLongClutch.currentFilterOrNull.value as string === "true",
                userEnforcedDiscThickness: userEnforcedDiscThicknessFilterManager.currentFilterOrNull === null || userEnforcedDiscThicknessFilterManager.currentFilterOrNull.value === "" ? null : parseInt(userEnforcedDiscThicknessFilterManager.currentFilterOrNull.value as string),
            });
        }

    }, [
        bladeCountFilterManager.currentFilterOrNull,
        impellerDiameterFilterManager.currentFilterOrNull,
        bladeManufacturingFilterManager.currentFilterOrNull,
        shaftDiameterEditor.value,
        shaftLengthEditor.value,
        impellerHeightEditor.value,
        screwMEditor.value,
        threadLengthEditor.value,
        bladeProfileDisplayNameFilterManager.currentFilterOrNull,
        bladeAngleFilterManager.currentFilterOrNull,
        betaIncrementationFilterManager.currentFilterOrNull,
        preferredConstructionMaterialCodeFilterManager.currentFilterOrNull,
        preferredFastenersMaterialCodeFilterManager.currentFilterOrNull,
        rpmFilterManager.currentFilterOrNull,
        bladeEndSpeedFilterManager.currentFilterOrNull,
        powerAtWorkingPointFilterManager.currentFilterOrNull,
        totalPressureAtWorkingPointFilterManager.currentFilterOrNull,
        adjustableImpellerIdFilterManager.currentFilterOrNull,
        softStartPresentFilterManager.currentFilterOrNull,
        realizeWithClutchFilterManager.currentFilterOrNull,
        fanMountingPositionNameFilterManager.currentFilterOrNull,
        auditSeverityFilterManager.currentFilterOrNull,
        allowDemoDataFilterManager.currentFilterOrNull,
        allowTooLongClutch.currentFilterOrNull,
        ignoreMaximumTestedClutchManufacturingsFilterManager.currentFilterOrNull,
        userEnforcedDiscThicknessFilterManager.currentFilterOrNull
    ])


    return <>
        <Card header={"Złożenie wirnika"}
              subHeader={"Funkcja labolatoryjna do dokonywania mechanicznego złożenia wirnika (bez konkretnych warunków pracy i wykonania części symulacyjnej mechaniki)"}
              additionalClassNames={"mb-3"}>
            <div className="row">
                <div className="col-9">
                    <div className={"col-12 mb-3"}>
                        <div className={"row"}>
                            <div className="col-4 mb-3">
                                <AdditionalTextFilter fanNameFilterManager={bladeCountFilterManager}/>
                            </div>
                            <div className="col-4 mb-3">
                                <AdditionalTextFilter fanNameFilterManager={impellerDiameterFilterManager}/>
                            </div>
                            <div className="col-4 mb-3">

                            </div>
                            <div className="col-4 mb-3">
                                <AdditionalSelectFilter filterManager={bladeManufacturingFilterManager}/>
                            </div>
                            <div className="col-4 mb-3">
                                <AdditionalSelectFilter filterManager={preferredConstructionMaterialCodeFilterManager}/>
                            </div>
                            <div className="col-4 mb-3">
                                <AdditionalSelectFilter filterManager={preferredFastenersMaterialCodeFilterManager}/>
                            </div>

                            <div className="col-12 mb-3">
                                <AdditionalSelectFilter filterManager={fanMountingPositionNameFilterManager}/>
                            </div>

                            <div className="col-8 mb-3">
                                <AdditionalSelectFilter filterManager={auditSeverityFilterManager}/>
                            </div>
                            {hasDevTools === true && <div className="col-4 mb-3">
                                <AdditionalSelectFilter filterManager={allowDemoDataFilterManager}/>
                            </div>}
                            <div className="col-4 mb-3">
                                <AdditionalSelectFilter filterManager={allowTooLongClutch}/>
                            </div>
                            <div className="col-4 mb-3">
                                <AdditionalSelectFilter filterManager={ignoreMaximumTestedClutchManufacturingsFilterManager}/>
                            </div>
                            <div className="col-4 mb-3">
                                <AdditionalTextFilter fanNameFilterManager={adjustableImpellerIdFilterManager}/>
                            </div>

                        </div>
                    </div>
                </div>
                <div className="col-3">
                    <p className={"text-muted pt-3"}>
                    Minimalny wymagany zestaw parametrów konstrukcji mechanicznej.
                    </p>
                    <p className={"text-muted pt-3"}>
                        (ID wirnika może być wpisane ręcznie lub ustawione przy przejściu z karty. Nie ma potrzeby podawania ilości łopat, jeżeli ID wirnika jest podane)
                    </p>
                    <p className={"text-muted pt-3"}>
                        Przy przejściu na Złożenie z karty, nazwa Pozycji Wirnika jest obliczana na podstawie wybranych kryteriów (pion/poziom, sprzęgło itp.) i nie zawsze może
                        być możliwy jej wybór (np. "Złożenie" nie obsługuje "VSO2"), domyślnie nastąpi fall-back do HSI1
                    </p>

                    <div className="d-flex gap-2 mt-4">
                        <AssemblyCriteriaUploader onUpload={setUploadedCriteria}/>
                        {criteria !== undefined && <AssemblyCriteriaDownloader criteria={criteria}/>}
                    </div>
                </div>

                <div className="col-12">
                    <div className="separator separator-dashed mb-4"/>
                </div>

                <div className={"col-12 mb-3"}>
                    <div className={"row"}>
                        <div className={"col-4 mb-3"}>
                            <LabeledEditor label={t("Średnica wału")} editor={shaftDiameterEditor} leftCols={5} rightCols={7}/>
                        </div>
                        <div className={"col-4 mb-3"}>
                            <LabeledEditor label={t("Długość wału")} editor={shaftLengthEditor} leftCols={5} rightCols={7}/>
                        </div>
                        <div className={"col-4 mb-3"}>
                            <LabeledEditor label={t("Wysokość wirnika")} editor={impellerHeightEditor} leftCols={5} rightCols={7}/>
                        </div>
                    </div>
                    <div className={"row"}>
                        <div className={"col-4 mb-3"}>
                            <LabeledEditor label={t("M Gwintu wału")} editor={screwMEditor} leftCols={5} rightCols={7}/>
                        </div>
                        <div className={"col-4 mb-3"}>
                            <LabeledEditor label={t("Dł. gwintu wału")} editor={threadLengthEditor} leftCols={5} rightCols={7}/>
                        </div>
                        <div className={"col-4 mb-3"}>
                            <p>(oba muszą być podane, by dobierac nietypowy gwint wału)</p>
                        </div>
                    </div>
                    <div className={"row"}>
                        <div className={"col-4 mb-3"}>
                            <AdditionalSelectFilter filterManager={bladeProfileDisplayNameFilterManager}/>
                        </div>
                        <div className={"col-4 mb-3"}>
                            <AdditionalTextFilter fanNameFilterManager={bladeAngleFilterManager}/>
                        </div>
                        {/* <div className={"col-4 mb-3"}>
                            <AdditionalTextFilter fanNameFilterManager={betaIncrementationFilterManager}/>
                        </div>*/}

                        <div className={"col-4 mb-3"}>
                            <AdditionalTextFilter fanNameFilterManager={rpmFilterManager}/>
                        </div>
                        <div className={"col-4 mb-3"}>
                            <AdditionalTextFilter fanNameFilterManager={bladeEndSpeedFilterManager}/>
                        </div>
                        <div className={"col-4 mb-3"}>
                            <AdditionalTextFilter fanNameFilterManager={userEnforcedDiscThicknessFilterManager}/>
                        </div>
                    </div>
                </div>


                <div className="col-12">
                    <div className="separator separator-dashed mb-4"/>
                </div>

                <div className={"col-9 mb-3"}>
                    <div className={"row"}>
                        <div className={"col-4 mb-3"}>
                            <AdditionalTextFilter fanNameFilterManager={powerAtWorkingPointFilterManager}/>
                        </div>

                        <div className={"col-4 mb-3"}>
                            <AdditionalTextFilter fanNameFilterManager={totalPressureAtWorkingPointFilterManager}/>
                        </div>

                        <div className={"col-4 mb-3"}>
                            <AdditionalSelectFilter filterManager={softStartPresentFilterManager}/>
                            <AdditionalSelectFilter filterManager={realizeWithClutchFilterManager}/>
                        </div>

                    </div>
                </div>
                <div className={"col-3"}>
                    <p className={"text-muted pt-3"}>
                        Kryterium przeprowadzenia nie tylko złożenia, ale też symulacji mechanicznej (uwaga - tylko przy
                        podanym także rpm).
                    </p>
                </div>

                <div className="col-12">
                    <div className="separator separator-dashed mb-4"/>
                </div>

                <p className={"text-muted pt-3"}>
                    W przypadku braku zadania średnicy wału, ilość przeszukiwanych manufakturyzacji sprzęgła zostanie domyuślnie ograniczona do sprawdzenia "Maks. ilość testowanych sprzęgieł bez zadania śr. wału" z ustawień administracyjnych.
                    Limit ten można chwilowo obejść przez wybór "Ignoruj 'Maks. ilość testowanych sprzęgieł" w kryteriach, jednak może on się wiązać z długim czasem doboru lub przekroczeniem dozwolonego czasu, a także
                    ogromem wyprowadzonych kombinacji złożeń na interfejs użytkownika. W praktyce może to oznaczać, że przy braku zadaniu średnicy wału, pokazywane będą tylko rozwiązania S50A.
                </p>
            </div>
        </Card>

        {loading === true && <Card additionalClassNames={"mb-3"}>
            <div className={" d-flex justify-content-center"}>
                <UboldSpinner/>
                <div style={{minHeight: "800px"}} data-role="improves experience on live search - removes jumping scroll"/>
            </div>
        </Card>}

        {h3RangeEstimation !== "loading" && criteria !== undefined && <ImpellerAssemblyH3EstimationResultCard estimation={h3RangeEstimation}/>}

        {!loading && h3RangeEstimation !== "loading" && <ImpellerAssemblyResults choices={choices} selectionH3RangeEstimation={h3RangeEstimation}/>}

        {loading === false && errorTree !== undefined && <Card additionalClassNames={"mb-3 mt-3"} header={"Przebieg złożenia / drzewo błędów"}><ErrorTreePresenter root={errorTree}/></Card>}

        {loading === false && mainCalculations.length > 0 && <Card header={"Obliczenia"}
                                                                   subHeader={"Obliczenia toku ogólnego konstrukcji wirników (bez obliczeń dla każdego konkretnego złożenia)"}
                                                                   additionalClassNames={"mb-3"}>
            <FormattedMechanicalSelectionCalculationLog calculations={mainCalculations}/>
        </Card>}

    </>;
}


export function ImpellerAssemblyResults({choices, selectionH3RangeEstimation}: { choices: MechanicalSelectionChoiceJson[], selectionH3RangeEstimation: MechanicalSelectionH3RangeEstimationJson | null }) {
    const temporaryIds = Array.from(new Set(choices.map(ch => ch.impellerAssembly.temporaryIdentity)));
    const choicePresentersByTemporaryId = temporaryIds.map(tempId => <ImpellerAssemblyResultGroup
        choices={choices.filter(ch => ch.impellerAssembly.temporaryIdentity === tempId)}
        selectionH3RangeEstimation={selectionH3RangeEstimation}/>);

    return <>{choicePresentersByTemporaryId}</>;
}

export function ImpellerAssemblyResultGroup({choices, selectionH3RangeEstimation}: { choices: MechanicalSelectionChoiceJson[], selectionH3RangeEstimation: MechanicalSelectionH3RangeEstimationJson | null }) {
    return <>
        <ImpellerAssemblyResultsForForm choices={choices} selectionH3RangeEstimation={selectionH3RangeEstimation}/>

    </>
}

ImpellerAssemblyPage.propTypes = {}

ImpellerAssemblyPage.defaultProps = {}

